import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AuthPageLayout, Button } from '@reed/design-system';
import { track, trackBeforeNavigation } from '@reed/analytics';
import { signupAuthorizeURL, dashboardURLFor, type UserType } from '../lib/auth-onboarding';
import { isSafeHref } from '../lib/validation';
import candidateHeart from '../assets/hands/candidate-heart.png';
import candidateHeartWebp from '../assets/hands/candidate-heart.webp';
import employerHand from '../assets/hands/employer-hand.png';
import employerHandWebp from '../assets/hands/employer-hand.webp';

interface Option {
  value: UserType;
  label: string;
  description: string;
  art: string;
  artWebp: string;
  artFrameClassName: string;
  artClassName: string;
}

const OPTIONS: Option[] = [
  {
    value: 'candidate',
    label: 'Candidate',
    description: 'Our AI finds your next role for you',
    art: candidateHeart,
    artWebp: candidateHeartWebp,
    artFrameClassName: 'h-[80px] w-[47px]',
    artClassName: 'h-[147.91%] left-[-117.96%] top-[-12.58%] w-[337.64%]',
  },
  {
    value: 'employer',
    label: 'Employer',
    description: 'Add jobs and discover top talent fast',
    art: employerHand,
    artWebp: employerHandWebp,
    artFrameClassName: 'h-[80px] w-[65px]',
    artClassName: 'h-[175.57%] left-[-51.35%] top-[-17.33%] w-[213.8%]',
  },
];


export default function OnboardingLandingPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<UserType>('candidate');

  // A WorkOS callback whose CSRF nonce cookie expired fails closed and restarts
  // signup here: return_to is the public-site origin, and /login and /signup
  // preserve the query and bounce to this landing route.
  const errorKey = searchParams.get('error');
  const errorMessage =
    errorKey === 'session_expired'
      ? 'Your sign-up session expired. Please choose your account type and try again — if this keeps happening, check that your browser allows cookies.'
      : errorKey
        ? 'Something went wrong. Please try again.'
        : null;

  function handleContinue(): void {
    if (selected === 'employer') {
      void track('account_type_selected', { account_type: 'employer' });
      navigate('/onboarding/employer');
      return;
    }
    const url = signupAuthorizeURL('candidate');
    if (isSafeHref(url)) {
      trackBeforeNavigation('account_type_selected', { account_type: 'candidate' }, () => window.location.assign(url));
    }
  }

  const loginUrl = `${dashboardURLFor(selected)}/login`;

  return (
    <AuthPageLayout
      panelLabel="Choose an account type"
      heroTitle="Your hire power"
      heroSubtitle="Stop searching, start matching"
    >
      <div className="flex w-full flex-col gap-6">
        {errorMessage && (
          <Alert
            variant="error"
            size="mobile"
            message={errorMessage}
            className="w-full items-start rounded-[12px] px-3 [&>svg]:shrink-0"
          />
        )}

        <div className="flex flex-col gap-2 text-center">
          <h1 className="font-display text-[24px] font-semibold leading-[1.3] text-brand-piccadilly-blue">
            How will you use Reed.ai?
          </h1>
          <p className="font-body text-[14px] leading-[1.5] text-[#474749]">
            Choose your account type to get the right experience.
          </p>
        </div>

        <fieldset className="flex flex-col gap-4">
          <legend className="sr-only">Account type</legend>
          {OPTIONS.map((opt) => {
            const checked = selected === opt.value;
            return (
              <label
                key={opt.value}
                className={
                  'flex h-[90px] w-full cursor-pointer items-center rounded-[12px] border-[1.5px] pr-3 transition-colors focus-within:outline focus-within:outline-1 focus-within:outline-offset-2 focus-within:outline-brand-dark-carnaby-pink ' +
                  (checked
                    ? 'border-brand-carnaby-pink bg-brand-carnaby-pink/5'
                    : 'border-[#DDDFED] bg-white hover:border-brand-piccadilly-blue')
                }
              >
                <input
                  type="radio"
                  name="account-type"
                  value={opt.value}
                  checked={checked}
                  onChange={() => setSelected(opt.value)}
                  className="sr-only"
                  aria-label={opt.label}
                />
                <span
                  className="pointer-events-none flex h-[90px] w-[79px] shrink-0 items-end justify-center overflow-hidden py-[1.5px]"
                  aria-hidden="true"
                >
                  <span className={`relative shrink-0 ${opt.artFrameClassName}`}>
                    <picture className={`absolute max-w-none select-none ${opt.artClassName}`}>
                      <source srcSet={opt.artWebp} type="image/webp" />
                      <img
                        src={opt.art}
                        alt=""
                        className="h-full w-full object-contain"
                        draggable={false}
                      />
                    </picture>
                  </span>
                </span>
                <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 leading-[1.5]">
                  <span className="font-body text-[16px] font-medium text-brand-piccadilly-blue">
                    {opt.label}
                  </span>
                  <span className="font-body text-[14px] text-[#181818]">{opt.description}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <div className="flex flex-col gap-4">
          <Button variant="primary" className="h-[52px] w-full" onClick={handleContinue}>
            Continue
          </Button>

          <p className="text-center font-body text-[14px] leading-[1.5] text-[#474749]">
            Already have an account?{' '}
            <a href={loginUrl} className="font-medium text-brand-dark-carnaby-pink hover:underline">
              log in
            </a>
          </p>
        </div>
      </div>
    </AuthPageLayout>
  );
}
